/**
 * OrgEntityConfigService
 *
 * Manages per-organization entity configuration:
 *   1. Active field groups (global per org) — org/{orgId}/config-entity/active-groups
 *   2. Repository overrides (per entity)    — org/{orgId}/config-repository/{entityName}
 *   3. Field-audit retention overrides      — org/{orgId}/config-entity/audit-retention
 *
 * Both paths use the repository factory (Firestore / InMemory cache) following the same
 * pattern as other domain services. Firebase is not touched directly here.
 *
 * Repository overrides path matches what repositoryResolver.loadConfigs() reads, so
 * after setRepositoryOverride / clearRepositoryOverride, the caller must trigger
 * refreshConfigs() (via useRepositoryResolver) to pick up the change.
 */
import { rtLogService } from '@rammetec/core';
import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { ORG_ACTIVE_GROUPS, ORG_AUDIT_RETENTION, ORG_REPOSITORY_CONFIG, } from '../types/orgEntityConfig.js';
const ACTIVE_GROUPS_DOC_ID = 'active-groups';
const AUDIT_RETENTION_DOC_ID = 'audit-retention';
class OrgEntityConfigService {
    // ─── Repos helpers ────────────────────────────────────────────────────────
    async getActiveGroupsRepo(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/config-entity`, ORG_ACTIVE_GROUPS, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    async getRepositoryConfigRepo(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/config-repository`, ORG_REPOSITORY_CONFIG, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    async getAuditRetentionRepo(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/config-entity`, ORG_AUDIT_RETENTION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    // ─── Active groups ────────────────────────────────────────────────────────
    /**
     * Returns the org's active field groups, or an empty array if not configured.
     */
    async getActiveGroups(orgId) {
        try {
            const repo = await this.getActiveGroupsRepo(orgId);
            const doc = await repo.getById(ACTIVE_GROUPS_DOC_ID);
            return doc?.groups ?? [];
        }
        catch (error) {
            rtLogService.error('[OrgEntityConfigService] getActiveGroups failed', { orgId, error });
            return [];
        }
    }
    /**
     * Persists the org's active field groups (full replace).
     */
    async setActiveGroups(orgId, groups) {
        const repo = await this.getActiveGroupsRepo(orgId);
        const existing = await repo.getById(ACTIVE_GROUPS_DOC_ID);
        if (existing) {
            await repo.update(ACTIVE_GROUPS_DOC_ID, { groups });
        }
        else {
            await repo.create({ id: ACTIVE_GROUPS_DOC_ID, groups });
        }
        rtLogService.info('[OrgEntityConfigService] setActiveGroups', { orgId, groups });
    }
    // ─── Repository overrides ─────────────────────────────────────────────────
    /**
     * Returns all per-entity repository config overrides for the org.
     * Keys are entity names; values are { dataSource, cache }.
     */
    async getRepositoryOverrides(orgId) {
        try {
            const repo = await this.getRepositoryConfigRepo(orgId);
            return await repo.getAll();
        }
        catch (error) {
            rtLogService.error('[OrgEntityConfigService] getRepositoryOverrides failed', { orgId, error });
            return [];
        }
    }
    /**
     * Saves (upsert) a per-entity repository override.
     * After calling this, trigger refreshConfigs() on the RepositoryResolverContext so
     * in-flight repositories pick up the new strategy.
     */
    async setRepositoryOverride(orgId, entityName, cfg) {
        const repo = await this.getRepositoryConfigRepo(orgId);
        const existing = await repo.getById(entityName);
        if (existing) {
            await repo.update(entityName, { dataSource: cfg.dataSource, cache: cfg.cache });
        }
        else {
            await repo.create({ id: entityName, dataSource: cfg.dataSource, cache: cfg.cache });
        }
        rtLogService.info('[OrgEntityConfigService] setRepositoryOverride', { orgId, entityName, cfg });
    }
    /**
     * Removes a per-entity repository override (reverts to the entity's default strategy).
     * After calling this, trigger refreshConfigs() on the RepositoryResolverContext.
     */
    async clearRepositoryOverride(orgId, entityName) {
        const repo = await this.getRepositoryConfigRepo(orgId);
        await repo.delete(entityName);
        rtLogService.info('[OrgEntityConfigService] clearRepositoryOverride', { orgId, entityName });
    }
    // ─── Field-audit retention overrides ──────────────────────────────────────
    /**
     * Returns per-aggregate retention overrides (days). Empty object when unset.
     * Effective retention is applied at purge time (request 0252 / spec 0046 §10.3).
     */
    async getAuditRetentionOverrides(orgId) {
        try {
            const repo = await this.getAuditRetentionRepo(orgId);
            const doc = await repo.getById(AUDIT_RETENTION_DOC_ID);
            return doc?.byAggregate ?? {};
        }
        catch (error) {
            rtLogService.error('[OrgEntityConfigService] getAuditRetentionOverrides failed', {
                orgId,
                error,
            });
            return {};
        }
    }
    /**
     * Sets or clears a retention override for one aggregate entity.
     * `days === null` removes the key (org falls back to descriptor / default).
     */
    async setAuditRetentionOverride(orgId, aggregateEntity, days) {
        const repo = await this.getAuditRetentionRepo(orgId);
        const existing = await repo.getById(AUDIT_RETENTION_DOC_ID);
        const byAggregate = { ...(existing?.byAggregate ?? {}) };
        if (days == null) {
            delete byAggregate[aggregateEntity];
        }
        else {
            byAggregate[aggregateEntity] = days;
        }
        if (existing) {
            await repo.update(AUDIT_RETENTION_DOC_ID, { byAggregate });
        }
        else {
            await repo.create({ id: AUDIT_RETENTION_DOC_ID, byAggregate });
        }
        rtLogService.info('[OrgEntityConfigService] setAuditRetentionOverride', {
            orgId,
            aggregateEntity,
            days,
        });
    }
}
export const orgEntityConfigService = new OrgEntityConfigService();
//# sourceMappingURL=orgEntityConfigService.js.map